import React from 'react';

import { graphql } from 'gatsby';

import { styled, Theme } from '@mui/material';

import { MewaCircleType } from '@mewa/types';
import { MewaCircle, MewaCircleData } from '@mewa/ui';

import { StrapiMewaCircleCertainVariant } from '../../../templates/dynamic-content-page/content-components/mewa-circle-certain-variant';
import '../../../templates/dynamic-content-page/content-components/shared-mewa-circle';
import { StrapiComponentType } from '../types';
import { Description, TextContainer } from './mewa-essentials';
import { MewaEssentialsSection } from './types';

export const MewaCircleFragment = graphql`
  fragment MewaCircleEssentialsFragment on STRAPI__COMPONENT_MEWA_ESSENTIALS_MEWA_CIRCLE {
    tabTitle
    description
    circle {
      ...MewaCircleFragment
    }
    strapi_component
  }
`;

export const componentName = 'mewa-essentials.mewa-circle';

export type MewaEssentialsMewaCircleData = StrapiComponentType<
  typeof componentName
> & {
  description: string;
  circle: MewaCircleData | null;
};

export type MewaCircleCertainVariant = MewaEssentialsMewaCircleData & {
  locale: string;
  circleVariant: MewaCircleType;
};

export type MewaCircle =
  | MewaEssentialsMewaCircleData
  | MewaCircleCertainVariant;

export const isMewaCircle = (
  component: MewaEssentialsSection,
): component is MewaEssentialsSection<MewaCircle> => {
  return component.strapi_component === componentName;
};

const isMewaCircleWithCertainVariant = (
  mewaCircle: MewaCircle,
): mewaCircle is MewaCircleCertainVariant => {
  return 'locale' in mewaCircle && 'circleVariant' in mewaCircle;
};

export const StrapiMewaCircle = ({ data }: { data: MewaCircle }) => (
  <>
    <TextContainer>
      <Description>{data.description}</Description>
    </TextContainer>
    {isMewaCircleWithCertainVariant(data) ? (
      <StyledMewaCircleCertainVariant
        circleVariant={data.circleVariant}
        locale={data.locale}
      />
    ) : data.circle !== null ? (
      <StyledMewaCircle circle={data.circle} />
    ) : null}
  </>
);

const mewaCircleMargins = ({ theme }: { theme: Theme }) => ({
  [theme.breakpoints.up('sm')]: {
    marginTop: '42px',
  },

  [theme.breakpoints.up('mdSm')]: {
    marginTop: '94px',
  },
});

const StyledMewaCircleCertainVariant = styled(StrapiMewaCircleCertainVariant)(
  mewaCircleMargins,
);

const StyledMewaCircle = styled(MewaCircle)(mewaCircleMargins);
